import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { initializeAggregations, initializeAggregationsSuccess } from '../../store/actions/aggregations.actions'
import { Store } from '@ngrx/store'
import { concatLatestFrom } from '@ngrx/operators'
import { map } from 'rxjs/operators'
import { MetricFinderKey } from '@core/enums/metric.enums'
import {
  selectAggregation,
  selectAggregationContextMetadata,
  selectAggregationsContext,
  selectAggregationsLeftAggregation,
  selectAggregationsRightAggregation
} from '../../store/selectors/aggregations.selectors'
import {
  AGGREGATIONS_AVERAGE_SELECTABLE_MODE,
  AGGREGATIONS_COMPETITION_AVERAGE_SELECTABLE_MODE,
  AGGREGATIONS_MATCH_SELECTABLE_MODE
} from '../../constants/aggregation.constants'
import { AggregationsState } from '../../store/states/aggregations.state'
import { AggregationContext, Aggregations, AggregationSelectable } from '../../models/aggregations.models'

@Injectable()
export class AggregationsEffects {
  private readonly _store: Store = inject(Store)
  private readonly _actions$ = inject(Actions)

  initializeAggregations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(initializeAggregations),
      concatLatestFrom(() => [
        this._store.select(selectAggregationsLeftAggregation),
        this._store.select(selectAggregationsRightAggregation),
        this._store.select(selectAggregation),
        this._store.select(selectAggregationsContext),
        this._store.select(selectAggregationContextMetadata)
      ]),
      map(
        ([
          initialState,
          currentLeftAggregation,
          currentRightAggregation,
          currentAggregation,
          context,
          contextMetadata
        ]) => {
          const { type: _type, ...newState } = { ...initialState }

          const { leftAggregation, rightAggregation, aggregation } = this._resetAggregationsByContext(
            newState,
            currentLeftAggregation,
            currentRightAggregation,
            currentAggregation,
            context,
            contextMetadata
          )

          const { left, right, simple } = this._resolveDefaultAggregations(
            leftAggregation,
            rightAggregation,
            aggregation,
            newState.isMatch,
            newState.isVsEnabled
          )

          const aggregationState: AggregationsState = {
            ...(newState as AggregationsState),
            leftAggregation: left,
            rightAggregation: right,
            aggregation: simple
          }

          return initializeAggregationsSuccess({ newState: aggregationState })
        }
      )
    )
  )

  private _resetAggregationsByContext(
    newState: Partial<AggregationsState>,
    leftAggregation: AggregationSelectable,
    rightAggregation: AggregationSelectable,
    aggregation: AggregationSelectable,
    oldContext: MetricFinderKey,
    contextMetadata: AggregationContext
  ): Aggregations {
    let _leftAggregation = leftAggregation
    let _rightAggregation = rightAggregation
    let _aggregation = aggregation

    if (!oldContext) {
      return newState
    }

    if (newState.isVsEnabled && !contextMetadata?.isVsEnabled) {
      _aggregation = undefined
    }

    if (newState.context !== oldContext) {
      _aggregation = undefined
      _leftAggregation = undefined
      _rightAggregation = undefined
    }

    return {
      leftAggregation: _leftAggregation,
      rightAggregation: _rightAggregation,
      aggregation: _aggregation
    }
  }

  private _resolveDefaultAggregations(
    leftAggregation: AggregationSelectable,
    rightAggregation: AggregationSelectable,
    aggregation: AggregationSelectable,
    isMatch: boolean,
    isComparison: boolean
  ): { left: AggregationSelectable; right: AggregationSelectable; simple: AggregationSelectable } {
    let left: AggregationSelectable = leftAggregation
    let right: AggregationSelectable = rightAggregation
    let simple: AggregationSelectable = aggregation

    if (!leftAggregation) {
      left = isMatch ? AGGREGATIONS_MATCH_SELECTABLE_MODE : AGGREGATIONS_AVERAGE_SELECTABLE_MODE
    }

    if (!rightAggregation) {
      right = isMatch
        ? AGGREGATIONS_AVERAGE_SELECTABLE_MODE
        : isComparison
          ? AGGREGATIONS_AVERAGE_SELECTABLE_MODE
          : AGGREGATIONS_COMPETITION_AVERAGE_SELECTABLE_MODE
    }

    if (!aggregation) {
      simple = isMatch ? AGGREGATIONS_MATCH_SELECTABLE_MODE : AGGREGATIONS_AVERAGE_SELECTABLE_MODE
    }

    return { left, right, simple }
  }
}
